import { useEffect, useMemo, useRef } from 'react';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { MediaOptions } from '@atlaskit/editor-plugin-media/types';
import { getMediaProvider } from '@atlassian/company-hub-common';

import { IS_UPLOAD_MEDIA_CONFIG_REQUIRED } from '../../constants';
import { type HubEditorAPI } from '../../types';

export const useMediaOptions = ({
	contentId,
	editorAPI,
	customMediaProvider,
	mediaConfig,
}: {
	contentId?: string;
	customMediaProvider?: Promise<MediaProvider> | undefined;
	editorAPI?: HubEditorAPI;
	mediaConfig?: {
		asapIssuer: string;
		mediaCollectionName: string;
		mediaToken: string;
		mediaUrl: string;
	};
}): MediaOptions => {
	const resolver = useRef<(provider: MediaProvider) => void>();

	useEffect(() => {
		if (!contentId) {
			return;
		}

		const createMediaProvider = () => {
			return getMediaProvider(
				mediaConfig?.mediaCollectionName ?? '',
				mediaConfig?.asapIssuer ?? '',
				mediaConfig?.mediaToken ?? '',
				mediaConfig?.mediaUrl ?? '',
				IS_UPLOAD_MEDIA_CONFIG_REQUIRED,
			);
		};

		const mediaProvider = createMediaProvider();
		if (mediaProvider) {
			if (editorAPI?.media?.actions) {
				editorAPI.media.actions.setProvider(Promise.resolve(mediaProvider));
			}
			if (resolver.current) {
				resolver.current(mediaProvider);
			}
		}
	}, [contentId, mediaConfig, editorAPI]);

	return useMemo(() => {
		const provider =
			customMediaProvider ??
			new Promise<MediaProvider>((resolve) => {
				resolver.current = resolve;
			});

		return {
			provider,
			allowMediaSingle: true,
			allowMediaGroup: true,
			allowResizing: true,
			allowLinking: true,
			allowResizingInTables: true,
			allowAltTextOnImages: true,
			allowMediaSingleEditable: true,
			allowRemoteDimensionsFetch: true,
			allowMarkdownVideo: true,
			allowCaptions: true,
			allowAnnotation: true,
			allowLazyLoading: true,
			allowAdvancedToolBarOptions: true,
			allowDropzoneDropLine: true,
			allowMediaInline: true,
			allowTemplatePlaceholders: true,
			featureFlags: {},
		};
	}, [customMediaProvider]);
};
